import React, { useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { Modal, ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight'
import { apiClient } from 'pobble-components/dist/api'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import classNames from 'classnames'
import posthog from 'posthog-js'

dayjs.extend(isoWeek)

const getWeekMonday = date => dayjs(date).startOf('isoWeek')

const formatWeekRange = monday => {
  const friday = monday.add(4, 'day')
  const sameYear = monday.year() === friday.year()
  const startFormat = sameYear ? 'D MMM' : 'D MMM YYYY'
  const endFormat = sameYear ? 'D MMM' : 'D MMM YYYY'
  return `${monday.format(startFormat)} – ${friday.format(endFormat)}`
}

const styles = {
  header: {
    padding: '24px 24px 0',
    textAlign: 'left'
  },
  title: {
    margin: 0,
    font: 'var(--heading--)',
    fontWeight: 'bold',
    color: 'var(--gray)',
    lineHeight: 1.3
  },
  subtitle: {
    margin: '8px 0 0',
    font: 'var(--body)',
    color: 'var(--gray-)',
    lineHeight: 1.4
  },
  content: {
    padding: '20px 24px 8px'
  },
  weekNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--light-gray--)',
    borderRadius: '8px',
    padding: '4px 8px',
    marginBottom: '16px'
  },
  weekLabel: {
    margin: 0,
    font: 'var(--body)',
    fontWeight: 'bold',
    color: 'var(--gray)'
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    color: 'var(--blue)',
    fontSize: '18px',
    '&:hover:not(:disabled)': {
      background: 'rgba(0, 174, 239, 0.1)'
    },
    '&:disabled': {
      color: 'var(--gray--)',
      cursor: 'default'
    }
  },
  days: {
    display: 'flex',
    gap: '8px'
  },
  dayCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minHeight: '96px',
    padding: '12px 4px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    background: '#fff',
    color: 'var(--gray)',
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover:not(:disabled)': {
      borderColor: 'var(--dark-blue)',
      background: 'hsl(225deg 59% 97%)'
    },
    '&:disabled': {
      borderColor: 'transparent',
      color: 'var(--gray--)',
      cursor: 'default',
      background: 'transparent'
    }
  },
  dayCardSelected: {
    borderColor: 'var(--dark-blue) !important',
    color: 'var(--dark-blue) !important',
    background: 'hsl(225deg 59% 97%)',
    fontWeight: 'bold'
  },
  dayName: {
    font: 'var(--body-)',
    lineHeight: 1.2
  },
  dayNumber: {
    font: 'var(--heading--)',
    fontWeight: 'bold',
    lineHeight: 1.2
  },
  dayMonth: {
    font: 'var(--body-)',
    lineHeight: 1.2
  },
  todayLabel: {
    marginTop: '4px',
    font: 'var(--body--)',
    fontSize: '12px',
    color: 'var(--gray-)',
    lineHeight: 1
  },
  footer: {
    display: 'flex',
    gap: '12px',
    width: '100%',
    '& > *': {
      flex: 1,
      justifyContent: 'center'
    }
  },
  notNow: {
    color: 'var(--gray) !important'
  }
}

export const SaveForLaterModal = injectSheet(styles)(
  function SaveForLaterModal({
    classes,
    isOpen,
    requestClose,
    presentation,
    onSaved,
    setDeliveryDateOnly = false,
    location
  }) {
    const {
      freeTrial,
      validPaymentMethod,
      hasAccessToTL
    } = useSubscriptionDetails()
    const currentWeekMonday = useMemo(() => getWeekMonday(dayjs()), [])
    const today = useMemo(() => dayjs().startOf('day'), [])

    const [weekMonday, setWeekMonday] = useState(currentWeekMonday)
    const [selectedDate, setSelectedDate] = useState(null)
    const [pending, setPending] = useState(false)

    const weekdays = useMemo(
      () => [0, 1, 2, 3, 4].map(offset => weekMonday.add(offset, 'day')),
      [weekMonday]
    )

    const canGoPrevious = weekMonday.isAfter(currentWeekMonday, 'day')

    const resetAndClose = () => {
      setWeekMonday(currentWeekMonday)
      setSelectedDate(null)
      setPending(false)
      requestClose()
    }

    const saveLesson = ({ withDate }) => {
      if (pending) return
      if (withDate && !selectedDate) return

      if (setDeliveryDateOnly && !withDate) {
        resetAndClose()
        return
      }

      posthog.capture(
        withDate
          ? setDeliveryDateOnly
            ? 'clicked on set delivery date from my lessons'
            : 'clicked on save lesson from save for later modal'
          : 'clicked on save without a date from save for later modal',
        {
          lesson_category: presentation?.category || null,
          lesson_id: presentation?.id || null,
          lesson_title: presentation?.title || null,
          freeTrial,
          validPaymentMethod,
          hasAccessToTL,
          location,
          ...(withDate && {
            saved_lesson_delivery_date: selectedDate.format('YYYY-MM-DD')
          })
        }
      )

      setPending(true)

      const deliveryDate = selectedDate && selectedDate.format('YYYY-MM-DD')

      const request = setDeliveryDateOnly
        ? apiClient.post(
            `/v3/presentations/${presentation.id}/set_delivery_date`,
            null,
            {
              params: {
                saved_lesson_delivery_date: deliveryDate
              }
            }
          )
        : withDate
          ? apiClient.post(
              `/v3/presentations/${presentation.id}/create_presentation_copy`,
              null,
              {
                params: {
                  saved_lesson_delivery_date: deliveryDate
                }
              }
            )
          : apiClient.post(
              `/v3/presentations/${presentation.id}/create_presentation_copy`
            )

      request
        .then(() => {
          onSaved({ withDate })
          resetAndClose()
        })
        .catch(error => {
          setPending(false)
          errorHandler(error)
        })
    }

    return (
      <Modal closeButton isOpen={isOpen} requestClose={resetAndClose}>
        <div className={classes.header}>
          <h2 className={classes.title}>When will you deliver this lesson?</h2>
          <p className={classes.subtitle}>
            Pick a day if you know it, or save without a date for now. <br />You
            can update this anytime.
          </p>
        </div>
        <ModalContent className={classes.content}>
          <div className={classes.weekNav}>
            <ButtonElement
              className={classes.navButton}
              disabled={!canGoPrevious || pending}
              onClick={() => setWeekMonday(date => date.subtract(1, 'week'))}
              aria-label="Previous week">
              <FontAwesomeIcon icon={faAngleLeft} />
            </ButtonElement>
            <p className={classes.weekLabel}>{formatWeekRange(weekMonday)}</p>
            <ButtonElement
              className={classes.navButton}
              disabled={pending}
              onClick={() => setWeekMonday(date => date.add(1, 'week'))}
              aria-label="Next week">
              <FontAwesomeIcon icon={faAngleRight} />
            </ButtonElement>
          </div>
          <div className={classes.days}>
            {weekdays.map(day => {
              const isPast = day.isBefore(today, 'day')
              const isToday = day.isSame(today, 'day')
              const isSelected = selectedDate && day.isSame(selectedDate, 'day')

              return (
                <button
                  key={day.format('YYYY-MM-DD')}
                  type="button"
                  disabled={isPast || pending}
                  className={classNames(classes.dayCard, {
                    [classes.dayCardSelected]: isSelected
                  })}
                  onClick={() => setSelectedDate(day)}>
                  <span className={classes.dayName}>{day.format('ddd')}</span>
                  <span className={classes.dayNumber}>{day.format('D')}</span>
                  {!isToday && (
                    <span className={classes.dayMonth}>
                      {day.format('MMM')}
                    </span>
                  )}
                  {isToday &&
                    !isPast && (
                      <span className={classes.todayLabel}>Today</span>
                    )}
                </button>
              )
            })}
          </div>
        </ModalContent>
        <ModalFooter>
          <div className={classes.footer}>
            <Button
              tall
              pending={pending}
              disabled={!selectedDate}
              onClick={() => saveLesson({ withDate: true })}>
              {setDeliveryDateOnly ? 'Save' : 'Save lesson'}
            </Button>
            <Button
              tall
              simple
              pending={pending}
              className={classes.notNow}
              onClick={() => saveLesson({ withDate: false })}>
              {setDeliveryDateOnly ? 'Cancel' : 'Save without a date'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    )
  }
)
